import React, { useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmCheckBox,
  CmFixedTd,
  CmFlex,
  CmHeadingPagination,
  CmLabelAndInput,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmText,
  CmTooltip,
  ReactSingleSelectInput,
  SelectForm,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { ommisionText } from '@/lib/util';
import {
  MenuManagementAllocatedMenusBulkRegisterModal,
  MenuManagementAllocatedMenusRegisterModal,
} from '@/pages/menu-managements/allocated-menus/components/Modal';
import {
  allocatedMenuTableThs,
  areUnique,
  MenuManagementDeliveryStore,
  UNIQUE_ERROR_MESSAGE,
  UpdateMenuSet,
} from '@/pages/menu-managements/allocated-menus/service';
import { SEARCH_AREA_ID } from '@/pages/menu-managements/components/SearchAndDrag';
import { routes } from '@/router';
import {
  useMenuManagementAllocatedMenuList,
  useMenuManagementBulkCreateAllocatedMenu,
  useMenuManagementUpdateAllocatedMenu,
} from '@/services/AllocatedMenu/hooks';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  SearchBrandMenuResponse,
  useMenuManagementBrandMenuSearch,
} from '@/services/BrandMenu/hooks/search/useMenuManagementBrandMenuSearch';
import {
  SearchBrandMenuHourResponse,
  useMenuManagementBrandMenuHourSearch,
} from '@/services/BrandMenuHour/hooks/search/useMenuManagementBrandMenuHourSearch';

const MenuManagementAllocatedMenusPage: React.FC = (): JSX.Element => {
  const { managerAccountMe } = useManagerAccountMe();
  const history = useHistory();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [allocatedDeliveryStoreMenus, setAllocatedDeliveryStoreMenus] =
    useState<MenuManagementDeliveryStore[]>([]);
  const [checkedMasterStoreIds, setCheckedMasterStoreIds] = useState<number[]>(
    []
  );
  const [isSelectAllMasterStore, setIsSelectAllMasterStore] =
    useState<boolean>(false);
  const [isOpenUpdateModal, setIsOpenUpdateModal] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsDragged] = useState<boolean>(false);

  const [isOpenBulkUpdateModal, setIsOpenBulkUpdateModal] =
    useState<boolean>(false);
  const [brandMenuOptions, setBrandMenuOptions] = useState<
    SearchBrandMenuResponse[]
  >([]);
  const [brandMenuHourOptions, setBrandMenuHourOptions] = useState<
    SearchBrandMenuHourResponse[]
  >([]);
  const [menuSets, setMenuSets] = useState<UpdateMenuSet[]>([]);
  const [targetDeliveryStore, setTargetDeliveryStore] =
    useState<MenuManagementDeliveryStore>({
      master_store_id: 0,
      master_store_name: '',
      menu_sets: [],
      updated_at: null,
    });
  const [searchCount, setSearchCount] = useState(0);
  const allocatedMenuLength = allocatedDeliveryStoreMenus.length;
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [targetAssociateBrandSelect, setTargetAssociateBrandSelect] =
    useState<SelectForm | null>({
      label: '',
      value: '',
    });
  const associateBrandId = targetAssociateBrandSelect?.value ?? '';
  const masterStoreIds = allocatedDeliveryStoreMenus.map(
    (allocatedMenu) => allocatedMenu.master_store_id
  );
  const [title, setTitle] = useState<string>('');
  const [timer, setTimer] = useState<number>();
  const allCheckedMasterStore =
    masterStoreIds.length === checkedMasterStoreIds.length;
  const { count, decrement, increment, customizeCount } = useCounter(1);
  const getListQuery = {
    associateBrandId,
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
    title,
  } as const;

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: targetAssociateBrandSelect,
    options: associateBrandSelectOptions,
    onChange: async (associateBrand: SelectForm | null) => {
      if (
        associateBrand === null ||
        associateBrand.value === targetAssociateBrandSelect?.value
      )
        return;
      const associateBrandId = associateBrand.value;
      setIsLoading(true);
      setTargetAssociateBrandSelect(associateBrand);
      const [{ allocated_menus, count }, resBrandMenus] = await Promise.all([
        useMenuManagementAllocatedMenuList({
          ...getListQuery,
          currentPage: 1,
          associateBrandId,
        }),
        useMenuManagementBrandMenuSearch({
          associate_brand_id: associateBrandId,
        }),
      ]);
      customizeCount(1);
      setSearchCount(count);
      setAllocatedDeliveryStoreMenus(allocated_menus);
      setIsLoading(false);
      setCheckedMasterStoreIds([]);
      setBrandMenuOptions(resBrandMenus);
    },
    onReset: () => {
      setTargetAssociateBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const handleOnDragEnd = (result: DropResult) => {
    const [reorderedItem] = menuSets.splice(result.source.index, 1);
    menuSets.splice(result.destination?.index ?? 0, 0, reorderedItem);
    setMenuSets(menuSets);
    setIsDragged((prev) => !prev);
  };

  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setTitle(title);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      setIsLoading(true);
      const { allocated_menus, count } =
        await useMenuManagementAllocatedMenuList({
          ...getListQuery,
          currentPage: 1,
          title,
        });
      customizeCount(1);
      setSearchCount(count);
      setAllocatedDeliveryStoreMenus(allocated_menus);
      setIsLoading(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const toFirstPage = async () => {
    setIsLoading(true);
    const { allocated_menus } = await useMenuManagementAllocatedMenuList({
      ...getListQuery,
    });
    setAllocatedDeliveryStoreMenus(allocated_menus);
    customizeCount(1);
    setIsLoading(false);
    setCheckedMasterStoreIds([]);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { allocated_menus } = await useMenuManagementAllocatedMenuList({
      ...getListQuery,
      currentPage: previousPage,
    });
    setAllocatedDeliveryStoreMenus(allocated_menus);
    decrement();
    setCheckedMasterStoreIds([]);
    setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { allocated_menus } = await useMenuManagementAllocatedMenuList({
      ...getListQuery,
      currentPage: nextPage,
    });
    setAllocatedDeliveryStoreMenus(allocated_menus);
    increment();
    setCheckedMasterStoreIds([]);
    setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { allocated_menus } = await useMenuManagementAllocatedMenuList({
      ...getListQuery,
      currentPage: lastPage,
    });
    setAllocatedDeliveryStoreMenus(allocated_menus);
    customizeCount(lastPage);
    setCheckedMasterStoreIds([]);
    setIsLoading(false);
  };

  const handleToggleMasterStoreCheck = (id: number) => {
    if (!checkedMasterStoreIds.includes(id)) {
      setCheckedMasterStoreIds([...checkedMasterStoreIds, id]);
    } else {
      const filterdCheckdIds = checkedMasterStoreIds.filter(
        (checkedId) => checkedId !== id
      );
      setCheckedMasterStoreIds(filterdCheckdIds);
    }
  };

  const handleMultipleCheck = () => {
    if (allCheckedMasterStore) {
      setCheckedMasterStoreIds([]);
    } else {
      setCheckedMasterStoreIds(masterStoreIds);
    }
  };

  const handleOpenBulkUpdateModal = () => {
    setMenuSets([]);
    setIsOpenBulkUpdateModal(true);
  };

  const handleOpenModal = (allocatedMenu: MenuManagementDeliveryStore) => {
    if (allocatedMenu.menu_sets.length > 0) {
      const newAllocatedMenus = allocatedMenu.menu_sets.map((menuSet) => {
        return {
          brand_menu_id: menuSet.brand_menu.id,
          brand_menu_hour_id: menuSet.brand_menu_hour.id,
          sort_index: 0, // 後でsortさせるのでここは適当な値を入れる。
        };
      });
      setMenuSets(newAllocatedMenus);
    }
    setTargetDeliveryStore(allocatedMenu);
    setIsOpenUpdateModal(true);
  };

  const handleChangeBrandMenu = (index: number, brandMenuId: string) => {
    const copyBrandMenus = [...menuSets];
    copyBrandMenus[index].brand_menu_id = brandMenuId;
    setMenuSets(copyBrandMenus);
  };
  const handleChangeBrandMenuHour = (
    index: number,
    brandMenuHourId: string
  ) => {
    const copyBrandMenus = [...menuSets];
    copyBrandMenus[index].brand_menu_hour_id = brandMenuHourId;
    setMenuSets(copyBrandMenus);
  };

  const handleAddMenuSet = () => {
    setMenuSets([
      ...menuSets,
      { sort_index: 1, brand_menu_hour_id: '', brand_menu_id: '' },
    ]);
  };

  const handleRemoveMenuSet = (brandMenuIndex: number) => {
    setMenuSets((prev) => {
      return prev.filter((_, index) => index !== brandMenuIndex);
    });
  };

  const handleUpdate = () => {
    (async () => {
      const isUnique = areUnique(menuSets);
      if (!isUnique) return alert(UNIQUE_ERROR_MESSAGE);
      setIsLoading(true);
      await useMenuManagementUpdateAllocatedMenu({
        master_store_id: targetDeliveryStore.master_store_id,
        menu_sets: menuSets.map((menuSet, index) => {
          return {
            ...menuSet,
            sort_index: index + 1,
          };
        }),
      });
      const { allocated_menus, count } =
        await useMenuManagementAllocatedMenuList({
          ...getListQuery,
        });
      customizeCount(1);
      setSearchCount(count);
      setCheckedMasterStoreIds([]);
      setIsLoading(false);
      alert('店舗メニュー割り当てを更新しました。');
      setAllocatedDeliveryStoreMenus(allocated_menus);
      setIsOpenUpdateModal(false);
    })();
  };

  const handleBulkCreate = () => {
    (async () => {
      const isUnique = areUnique(menuSets);
      if (!isUnique) return alert(UNIQUE_ERROR_MESSAGE);
      setIsLoading(true);
      await useMenuManagementBulkCreateAllocatedMenu({
        master_store_ids: checkedMasterStoreIds,
        menu_sets: menuSets.map((menuSet, index) => {
          return {
            ...menuSet,
            sort_index: index + 1,
          };
        }),
      });
      const { allocated_menus, count } =
        await useMenuManagementAllocatedMenuList({
          ...getListQuery,
        });
      customizeCount(1);
      setSearchCount(count);
      setCheckedMasterStoreIds([]);
      setIsLoading(false);
      alert('店舗メニュー割り当てを更新しました。');
      setAllocatedDeliveryStoreMenus(allocated_menus);
      setIsOpenBulkUpdateModal(false);
    })();
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);

  useEffect(() => {
    if (allCheckedMasterStore) setIsSelectAllMasterStore(true);
    if (checkedMasterStoreIds.length === 0) setIsSelectAllMasterStore(false);
  }, [checkedMasterStoreIds]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const associateBrands = await useAssociateBrandList();
      const associateBrandOptions = associateBrands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id.toString(),
        };
      });
      const { associateBrand } = LocalStorageHandler.get('menuManagement');
      const initialAssociateBrand =
        associateBrand !== undefined
          ? associateBrand
          : associateBrandOptions[0];
      LocalStorageHandler.set('menuManagement', {
        associateBrand: initialAssociateBrand,
      });
      const [{ allocated_menus, count }, resBrandMenus, resBrandMenuHours] =
        await Promise.all([
          useMenuManagementAllocatedMenuList({
            ...getListQuery,
            associateBrandId: initialAssociateBrand.value,
            currentPage: 1,
          }),
          useMenuManagementBrandMenuSearch({
            associate_brand_id: initialAssociateBrand.value,
          }),
          useMenuManagementBrandMenuHourSearch({
            associate_brand_id: initialAssociateBrand.value,
          }),
        ]);
      setAssociateBrandSelectOptions(associateBrandOptions);
      setBrandMenuOptions(resBrandMenus);
      setBrandMenuHourOptions(resBrandMenuHours);
      setTargetAssociateBrandSelect(initialAssociateBrand);
      customizeCount(1);
      setSearchCount(count);
      setAllocatedDeliveryStoreMenus(allocated_menus);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout
      title={routes.menuManagementAllocations.title}
      {...{ associateBrandSelectInput }}
    >
      <CmBox>
        <CmBox padding="20px 20px 20px 0" id={SEARCH_AREA_ID}>
          <CmFlex alignItems="flex-end" justifyContent="space-between" w="full">
            <CmFlex alignItems="flex-end" width="full">
              <CmBox width="40%">
                <CmLabelAndInput
                  bg="white"
                  id="title"
                  label="店舗名"
                  fontWeight="bold"
                  value={title}
                  onChange={handleInputName}
                />
              </CmBox>
            </CmFlex>
            <CmBox width="40%" textAlign="right">
              件数: {searchCount} 件
            </CmBox>
          </CmFlex>
        </CmBox>
        <CmFlex padding="20px 0 0" id={SEARCH_AREA_ID} />
        {allocatedMenuLength > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
          >
            <CmFlex justifyContent="space-between" mb="20px">
              <MenuManagementAllocatedMenusBulkRegisterModal
                masterStoreIds={checkedMasterStoreIds}
                handleOpenModal={handleOpenBulkUpdateModal}
                isOpen={isOpenBulkUpdateModal}
                handleCloseModal={() => setIsOpenBulkUpdateModal(false)}
                {...{
                  associateBrandId,
                  brandMenuOptions,
                  menuSets,
                  brandMenuHourOptions,
                  handleChangeBrandMenu,
                  handleChangeBrandMenuHour,
                  handleAddMenuSet,
                  handleRemoveMenuSet,
                  handleBulkCreate,
                  handleOnDragEnd,
                }}
              />
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTable
                isMultipleCheck
                ths={allocatedMenuTableThs}
                isSelectAll={isSelectAllMasterStore}
                {...{
                  handleMultipleCheck,
                }}
              >
                {allocatedDeliveryStoreMenus.map((allocatedMenu, index) => {
                  const {
                    master_store_id,
                    master_store_name,
                    menu_sets,
                    updated_at,
                  } = allocatedMenu;

                  const updatedAt =
                    updated_at !== null
                      ? DateDomainService.datetimeFormat(new Date(updated_at))
                      : '';

                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmFixedTd {...{ left: '0' }}>
                        <CmCheckBox
                          onChange={() => {
                            handleToggleMasterStoreCheck(master_store_id);
                          }}
                          isChecked={checkedMasterStoreIds.includes(
                            master_store_id
                          )}
                        />
                      </CmFixedTd>
                      <CmTd>
                        <CmBox
                          tabIndex={0}
                          cursor="pointer"
                          padding="5px"
                          onClick={() => handleOpenModal(allocatedMenu)}
                        >
                          <CmText color="blue.500">
                            <CmTooltip label={master_store_name}>
                              {ommisionText(master_store_name, 20)}
                            </CmTooltip>
                          </CmText>
                        </CmBox>
                      </CmTd>
                      <CmTd textAlign="center">
                        {menu_sets.map((menuSet, menuSetIndex) => {
                          const { brand_menu, brand_menu_hour } = menuSet;

                          const brandMenuTitleJa =
                            brand_menu.title.translation.ja;
                          const brandMenuHourTitleJa =
                            brand_menu_hour.title.translation.ja;
                          return (
                            <CmCard
                              key={menuSetIndex}
                              margin={
                                menuSetIndex !== 0 ? '10px auto 0' : '0 auto'
                              }
                              width={'100%'}
                              padding="2"
                              border="1px solid #EDF2F7"
                              justifyContent="space-around"
                            >
                              <CmFlex alignItems="center">
                                <CmBox
                                  margin="0 auto"
                                  padding="2px 5px"
                                  width="48%"
                                >
                                  <CmLink
                                    color="blue.500"
                                    to={`/menu-managements/menus/${brand_menu.id}/edit`}
                                  >
                                    <CmText fontSize="14px" isEllipsis>
                                      <CmTooltip label={brandMenuTitleJa}>
                                        {ommisionText(brandMenuTitleJa, 20)}
                                      </CmTooltip>
                                    </CmText>
                                  </CmLink>
                                </CmBox>
                                <CmBox
                                  margin="0 auto"
                                  padding="2px 5px"
                                  width="48%"
                                >
                                  <CmLink
                                    color="blue.500"
                                    to={`/menu-managements/business-hours/${brand_menu_hour.id}/edit`}
                                  >
                                    <CmText fontSize="14px" isEllipsis>
                                      <CmTooltip label={brandMenuHourTitleJa}>
                                        {ommisionText(brandMenuHourTitleJa, 20)}
                                      </CmTooltip>
                                    </CmText>
                                  </CmLink>
                                </CmBox>
                              </CmFlex>
                            </CmCard>
                          );
                        })}
                      </CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={updatedAt}>{updatedAt}</CmTooltip>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
              <MenuManagementAllocatedMenusRegisterModal
                isOpen={isOpenUpdateModal}
                handleCloseModal={() => setIsOpenUpdateModal(false)}
                {...{
                  brandMenuOptions,
                  menuSets,
                  brandMenus: menuSets,
                  targetDeliveryStore,
                  brandMenuHourOptions,
                  handleUpdate,
                  handleChangeBrandMenu,
                  handleChangeBrandMenuHour,
                  handleAddMenuSet,
                  handleRemoveMenuSet,
                  handleBulkCreate,
                  handleOnDragEnd,
                }}
              />
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致するメニュー割り当てが見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
      </CmBox>
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default MenuManagementAllocatedMenusPage;
